import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

const CHIP_GAP_PX = 4;
const MORE_LABEL_WIDTH_PX = 84;

export const CHIP_MEASURE_ATTR = 'data-chip';

type AutoFitChips = {
    chipRowRef: React.RefObject<HTMLDivElement>;
    visibleChipCount: number | null;
};

/**
 * Works out how many chips fit on one line.
 *
 * How many chips fit is a question about pixels, not a number that can be hardcoded — the same
 * control is wider on a large monitor than on a laptop. Every chip is rendered first and its
 * width cached, and the count is then derived from the row's own width. Caching the widths is
 * what makes this stable: a resize recomputes from the cache, so slicing the list can never
 * feed back into the measurement and oscillate.
 *
 * @param enabled   false leaves the caller in charge (fixed cap, or no cap at all)
 * @param itemCount number of chips the caller wants to show; a change invalidates the cache
 */
export const useAutoFitChips = (enabled: boolean, itemCount: number): AutoFitChips => {
    const chipRowRef = useRef<HTMLDivElement>(null);
    const chipWidthsRef = useRef<number[]>([]);
    const measuredCountRef = useRef(-1);
    const [visibleChipCount, setVisibleChipCount] = useState<number | null>(null);

    const fitChips = useCallback(() => {
        const row = chipRowRef.current;
        if (!row) return;
        let available = row.clientWidth;
        const parent = row.parentElement;
        if (parent) {
            const parentStyle = getComputedStyle(parent);
            available = parent.clientWidth - (parseFloat(parentStyle.paddingLeft) || 0) - (parseFloat(parentStyle.paddingRight) || 0);
        }

        if (!available) {
            setVisibleChipCount(null);
            return;
        }

        const widths = chipWidthsRef.current;
        let used = 0;
        let fits = 0;
        for (let index = 0; index < widths.length; index += 1) {
            const needed = used + widths[index] + (index > 0 ? CHIP_GAP_PX : 0);
            const budget = index === widths.length - 1 ? available : available - MORE_LABEL_WIDTH_PX - CHIP_GAP_PX;
            if (needed > budget) break;
            used = needed;
            fits += 1;
        }
        setVisibleChipCount(Math.max(fits, 1));
    }, []);

    useLayoutEffect(() => {
        if (!enabled) return;
        const row = chipRowRef.current;
        if (!row) return;

        if (measuredCountRef.current !== itemCount) {
            if (visibleChipCount !== null) {
                setVisibleChipCount(null);
                return;
            }
            chipWidthsRef.current = Array.from(row.querySelectorAll<HTMLElement>(`[${CHIP_MEASURE_ATTR}]`)).map(
                (chip) => chip.getBoundingClientRect().width,
            );
            measuredCountRef.current = itemCount;
        }
        fitChips();
    }, [enabled, visibleChipCount, itemCount, fitChips]);

    useEffect(() => {
        const row = chipRowRef.current;
        if (!enabled || !row || typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(() => fitChips());
        observer.observe(row);
        return () => observer.disconnect();
    }, [enabled, fitChips]);

    useEffect(() => {
        if (!enabled || typeof document === 'undefined' || !document.fonts?.ready) return;
        let cancelled = false;
        document.fonts.ready.then(() => {
            if (cancelled) return;
            measuredCountRef.current = -1;
            setVisibleChipCount(null);
        });
        return () => {
            cancelled = true;
        };
    }, [enabled]);

    return { chipRowRef, visibleChipCount: enabled ? visibleChipCount : null };
};
